import React, { useEffect, useState } from "react";

import api from "../services/api";

function guessKindFromUrlOrType(url, contentType) {
  const ct = String(contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return "image";
  if (ct.includes("pdf")) return "pdf";
  const clean = String(url || "").split("?")[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/.test(clean)) return "image";
  if (/\.pdf$/.test(clean)) return "pdf";
  return "unknown";
}

/**
 * Resolve a proof URL for download.
 * API paths like /api/v1/... are requested relative to axios baseURL.
 * Absolute http(s) URLs are used as-is.
 */
function toRequestConfig(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) {
    return { url: raw, absolute: true };
  }
  if (raw.startsWith("/api/v1/")) {
    return { url: raw.slice("/api/v1".length), absolute: false };
  }
  if (raw.startsWith("/")) {
    return { url: `${window.location.origin}${raw}`, absolute: true };
  }
  return { url: raw, absolute: true };
}

/**
 * In-app popup for deposit proof documents.
 * Loads auth-protected API file URLs via the shared API client (JWT + refresh).
 */
export default function DocumentPreviewModal({
  open,
  url = "",
  title = "Document",
  subtitle = "",
  loading = false,
  error = "",
  authRequired = false,
  contentHint = "",
  onClose,
}) {
  const [frameError, setFrameError] = useState("");
  const [blobUrl, setBlobUrl] = useState("");
  const [kind, setKind] = useState("unknown");
  const [loadingBlob, setLoadingBlob] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    setFrameError("");
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    setKind(guessKindFromUrlOrType(url, contentHint));
    if (!open || !url || loading || error) {
      setLoadingBlob(false);
      return undefined;
    }

    let cancelled = false;
    const req = toRequestConfig(url);
    if (!req) {
      setLoadingBlob(false);
      return undefined;
    }

    const needsAuth = Boolean(authRequired) || String(url).includes("/api/v1/");

    (async () => {
      setLoadingBlob(true);
      try {
        let blob;
        if (req.absolute && !needsAuth) {
          const res = await fetch(req.url, { credentials: "same-origin" });
          if (!res.ok) throw new Error(`Could not load document (${res.status}).`);
          blob = await res.blob();
        } else if (req.absolute && needsAuth) {
          const token = localStorage.getItem("accessToken");
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const res = await fetch(req.url, { headers, credentials: "same-origin" });
          if (!res.ok) throw new Error(`Could not load document (${res.status}).`);
          blob = await res.blob();
        } else {
          // Relative API path — use axios so expired JWT can refresh.
          const res = await api.get(req.url, { responseType: "blob" });
          blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
          // DRF JSON errors can arrive as blobs when responseType is blob.
          if (blob.type && blob.type.includes("application/json")) {
            const text = await blob.text();
            let detail = "Could not load document.";
            try {
              detail = JSON.parse(text)?.detail || detail;
            } catch {
              /* ignore */
            }
            throw new Error(detail);
          }
          if (!blob.size) {
            throw new Error("Document file was empty.");
          }
        }
        if (cancelled) return;
        if (!blob.size) {
          throw new Error("Document file was empty.");
        }
        const objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setKind(guessKindFromUrlOrType(url, blob.type || contentHint));
        setFrameError("");
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          setFrameError(
            status
              ? `Could not load document (${status}).`
              : err?.message || "Could not display this document."
          );
        }
      } finally {
        if (!cancelled) setLoadingBlob(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, open, loading, error, authRequired, contentHint]);

  useEffect(
    () => () => {
      if (blobUrl && blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
    },
    [blobUrl]
  );

  if (!open) return null;

  const previewSrc = blobUrl;
  const busy = loading || loadingBlob;

  return (
    <div
      className="modal-backdrop doc-preview-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      <div
        className="modal-card modal-card-elevated doc-preview-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="close-btn" onClick={onClose} aria-label="Close document preview">
          ×
        </button>
        <h3 id="doc-preview-title" style={{ marginTop: 0, marginRight: 36 }}>
          {title}
        </h3>
        {subtitle ? <p className="muted" style={{ marginTop: 0 }}>{subtitle}</p> : null}

        {busy ? <p className="muted">Loading document…</p> : null}
        {!busy && error ? (
          <p className="msg msg-error" role="alert">
            {error}
          </p>
        ) : null}
        {!busy && !error && frameError ? (
          <p className="msg msg-error" role="alert">
            {frameError}
          </p>
        ) : null}

        {!busy && !error && previewSrc ? (
          <div className="doc-preview-body">
            {kind === "image" ? (
              <img
                src={previewSrc}
                alt={title}
                className="doc-preview-image"
                onError={() => setFrameError("Could not display this image.")}
              />
            ) : (
              <iframe title={title} src={previewSrc} className="doc-preview-frame" />
            )}
            <div className="doc-preview-actions">
              <a
                className="btn-secondary"
                href={previewSrc}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </a>
              <button type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
